// src/components/UnifiedConfiguration.js

import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Settings, User, Book, ChevronRight, 
  Bell, Home, ArrowLeft, Brain,
  Check, AlertCircle
} from 'lucide-react';
import { Container } from './ui/Container';
//import { GradientButton } from './ui/GradientButton';
import BusinessProfileSection from './sections/BusinessProfileSection';
import ConfigurationSection from './sections/ConfigurationSection';
import KnowledgeBaseSection from './sections/KnowledgeBaseSection';
import CustomGreetingSection from './sections/CustomGreetingSection';
import PromptSettingsSection from './sections/PromptSettingsSection';

import { colors } from '../styles/designSystem';
import log from '../logger'; 
import { useTranslation } from 'react-i18next';



const Breadcrumb = ({ items }) => (
  <div className="flex items-center space-x-2 text-sm text-gray-600">
    <Link to="/" className="hover:text-violet-600 transition-colors">
      <Home className="w-4 h-4" />
    </Link>
    {items.map((item, index) => (
      <React.Fragment key={index}>
        <ChevronRight className="w-4 h-4 text-gray-400" />
        {index === items.length - 1 ? (
          <span className="text-violet-600 font-medium">{item.label}</span>
        ) : (
          <Link 
            to={item.path} 
            className="hover:text-violet-600 transition-colors"
          >
            {item.label}
          </Link>
        )}
      </React.Fragment>
    ))}
  </div>
);

const TabButton = ({ active, icon: Icon, label, onClick }) => (
  <button
    onClick={onClick}
    className={`
      flex items-center space-x-2 w-full px-4 py-3 rounded-lg transition-all
      ${active ? 
        'bg-violet-50 text-violet-600 shadow-sm' : 
        'hover:bg-gray-50 text-gray-600 hover:text-violet-600'
      }
    `}
  >
    <Icon className="w-5 h-5" />
    <span className="font-medium">{label}</span>
    {active && <ChevronRight className="w-4 h-4 ml-auto" />}
  </button>
);

const UnifiedConfiguration = ({ defaultTab = 'profile' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [activeSection, setActiveSection] = useState(defaultTab);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  // Effect to handle default tab and URL synchronization
  useEffect(() => {
    const path = location.pathname;

    const section = path.split('/').pop();
    if (section && ['profile', 'knowledge-base', 'prompt', 'custom-greeting', 'settings'].includes(section)) {
      setActiveSection(section);
    } else {
      setActiveSection(defaultTab);
    }
  }, [location, defaultTab]);

  // Tabs configuration
  const tabs = [
    {
      id: 'profile',
      label: t('UnifiedConfiguration.tabs.profile.label'),
      icon: User,
      path: '/configuration/profile',
      breadcrumb: t('UnifiedConfiguration.breadcrumb.business_profile')
    },
    {
      id: 'knowledge-base',
      label: t('UnifiedConfiguration.tabs.knowledge_base.label'),
      icon: Book,
      path: '/configuration/knowledge-base',
      breadcrumb: t('UnifiedConfiguration.breadcrumb.knowledge_base')
    },
    {
      id: 'prompt',
      label: t('UnifiedConfiguration.tabs.prompt.label'),
      icon: Brain,
      path: '/configuration/prompt',
      breadcrumb: t('UnifiedConfiguration.breadcrumb.prompt_settings')
    },
    {
      id: 'custom-greeting',
      label: t('UnifiedConfiguration.tabs.custom_greeting.label'),
      icon: Bell, // Use an appropriate icon from 'lucide-react'
      path: '/configuration/custom-greeting',
      breadcrumb: t('UnifiedConfiguration.breadcrumb.custom_greeting')
    },
    {
      id: 'settings',
      label: t('UnifiedConfiguration.tabs.settings.label'),
      icon: Settings,
      path: '/configuration/settings',
      breadcrumb: t('UnifiedConfiguration.breadcrumb.system_settings')
    }
  ];

  // Handle tab changes
  const handleTabChange = (tabId) => {
    const tab = tabs.find(t => t.id === tabId);
    if (tab) {
      setActiveSection(tabId);
      navigate(tab.path);
      log.debug('Tab changed to:', tabId);
    }
  };

  // Get current breadcrumb items
  const getCurrentBreadcrumb = () => {
    const currentTab = tabs.find(t => t.id === activeSection);
    return [
      { label: t('UnifiedConfiguration.breadcrumb.configuration'), path: '/configuration' },
      { label: currentTab?.breadcrumb || '', path: currentTab?.path || '' }
    ];
  };

  // Animation variants
  const pageTransition = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -20 }
  };

  return (
    <div className="min-h-screen">
      {/* Header Section */}
      <div className="bg-white border-b border-gray-200 sticky top-16 z-40">
        <Container className="py-6 px-4 sm:px-6 lg:px-20">
          <Breadcrumb items={getCurrentBreadcrumb()} />
          <div className="mt-4 flex items-center justify-between">
            <h1 className={`${colors.text.heading} text-2xl font-bold`}>
              {t('UnifiedConfiguration.title')}
            </h1>
            <button
              onClick={() => navigate(-1)}
              className="flex items-center text-gray-600 hover:text-violet-600 transition-colors"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              {t('UnifiedConfiguration.buttons.back')}
            </button>
          </div>
        </Container>
      </div>

      {/* Main Content */}
      <Container className="py-6 px-4 sm:px-6 lg:px-20">
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Sidebar Navigation */}
          <div className="lg:w-64 flex-shrink-0">
            <div className="bg-white rounded-xl shadow-sm p-2 sticky top-40">
              {tabs.map((tab) => (
                <TabButton
                  key={tab.id}
                  active={activeSection === tab.id}
                  icon={tab.icon}
                  label={tab.label}
                  onClick={() => handleTabChange(tab.id)}
                />
              ))}
            </div>
          </div>

          {/* Main Content Area */}
          <div className="flex-grow">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeSection}
                initial="initial"
                animate="animate"
                exit="exit"
                variants={pageTransition}
                transition={{ duration: 0.2 }}
              >
                {/* Alert Messages */}
                {(message || error) && (
                  <div className={`mb-6 p-4 rounded-xl ${
                    message ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'
                  }`}>
                    <div className="flex items-center">
                      {message ? (
                        <Check className="w-5 h-5 mr-2" />
                      ) : (
                        <AlertCircle className="w-5 h-5 mr-2" />
                      )}
                      <p>{message || error}</p>
                    </div>
                  </div>
                )}

                {/* Section Content */}
                <div className="bg-white rounded-xl shadow-sm">
                  {activeSection === 'profile' && (
                    <BusinessProfileSection 
                      onLoading={setLoading}
                      onMessage={setMessage}
                      onError={setError}
                    />
                  )}
                  {activeSection === 'settings' && (
                    <ConfigurationSection
                      onLoading={setLoading}
                      onMessage={setMessage}
                      onError={setError}
                    />
                  )}
                  {activeSection === 'knowledge-base' && (
                    <KnowledgeBaseSection
                      onLoading={setLoading}
                      onMessage={setMessage}
                      onError={setError}
                    />
                  )}
                  {activeSection === 'custom-greeting' && (
                    <CustomGreetingSection
                      onLoading={setLoading}
                      onMessage={setMessage}
                      onError={setError}
                    />
                  )}
                  {activeSection === 'prompt' && (
                    <PromptSettingsSection
                      onLoading={setLoading}
                      onMessage={setMessage}
                      onError={setError}
                    />
                  )}
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </Container>

      {/* Loading Overlay */}
      {loading && (
        <div className="fixed inset-0 bg-black/20 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="bg-white rounded-xl p-6 shadow-lg">
            <div className="animate-spin w-8 h-8 border-4 border-violet-600 border-t-transparent rounded-full" />
            <p className="mt-2 text-gray-600">{t('UnifiedConfiguration.alerts.loading_message')}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default UnifiedConfiguration;
